import type { RecordingCapture } from "./session-provenance";
import type { TrackingSport } from "@/lib/tracking/types";

export type RecordingStatus = {
  mode: "checking" | "consent" | "setup" | "live" | "finishing" | "save-failed" | "story" | "recovery" | "recovery-error";
  sessionId: string | null;
  offlineOwner?: string | null;
  capture?: RecordingCapture;
  sport: TrackingSport;
  paused: boolean;
  elapsedS: number;
  distanceKm: number;
};

export function retainRecording(status: RecordingStatus | null): boolean {
  return status !== null && ["live", "finishing", "save-failed", "recovery", "recovery-error"].includes(status.mode);
}

/** An active recorder keeps its launch parameters when another page changes the URL. */
export function recordingReturnHref(search: string): string {
  const source = new URLSearchParams(search);
  const query = new URLSearchParams();
  for (const key of ["base", "outingId", "outing", "demo", "native-feed", "fall-feed", "offline-pack", "install-demo"]) {
    const value = source.get(key);
    if (value && value.length <= 1024) query.set(key, value);
  }
  return `/companion/track${query.size ? `?${query}` : ""}`;
}

/** Pages other than /companion/track where a retained recording takes over the page. */
export const RECORDING_WORKSPACE_PATHNAMES = ["/companion/explore", "/companion/map"] as const;

export function isRecordingWorkspacePath(pathname: string | null | undefined): boolean {
  return (RECORDING_WORKSPACE_PATHNAMES as readonly string[]).includes(pathname ?? "");
}

export function recordingLabel(status: RecordingStatus): string {
  if (status.mode === "finishing") return "Saving your session";
  if (status.mode === "save-failed") return "Session needs saving";
  if (status.mode === "recovery" || status.mode === "recovery-error") return "Recording needs review";
  return status.paused ? "Recording paused" : "Recording your session";
}

export type RecordingLockRelease = () => Promise<void>;

/** Web Locks are released by the browser on tab/process death, without stale timers.
 * A replacement mount waits for its previous attempt to release completely. */
export async function acquireRecordingLock(
  manager: LockManager,
  previous: Promise<RecordingLockRelease | null> | null = null,
): Promise<RecordingLockRelease | null> {
  if (previous) {
    const release = await previous.catch(() => null);
    await release?.();
  }
  return new Promise((resolve, reject) => {
    const settled = manager.request("peakwave-active-recording", { mode: "exclusive", ifAvailable: true }, lock => {
      if (!lock) { resolve(null); return; }
      return new Promise<void>(release => resolve(async () => { release(); await settled; }));
    });
    void settled.catch(reject);
  });
}
